import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const ROLES = ['hunter', 'campaign_manager', 'admin_operator', 'management', 'admin'];

const emptyForm = { full_name: '', email: '', password: '', role: 'hunter' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Users = () => {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/users');
      setUsers(Array.isArray(response) ? response : []);
    } catch (error) {
      alert('Error loading users: ' + (error.response?.data?.error || error.message));
      setUsers([]);
    } finally { setLoading(false); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ full_name: user.full_name || '', email: user.email || '', role: user.role || 'hunter', password: '' });
  };

  const handleSave = async () => {
    if (!editingUser) return;
    try {
      await api.patch(`/api/users/${editingUser.id}`, formData);
      setEditingUser(null);
      setFormData(emptyForm);
      loadUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCreateUser = async () => {
    if (!formData.full_name || !formData.email || !formData.password) { alert('Semua field wajib diisi'); return; }
    if (formData.password.length < 6) { alert('Password minimal 6 karakter'); return; }
    try {
      await api.post('/api/users', formData);
      setShowCreateModal(false);
      setFormData(emptyForm);
      loadUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Hapus user ini? Tindakan tidak dapat dibatalkan.')) return;
    try {
      await api.delete(`/api/users/${userId}`);
      loadUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const f = (name) => <input type={name === 'password' ? 'password' : name === 'email' ? 'email' : 'text'}
    value={formData[name]}
    onChange={(e) => setFormData(p => ({ ...p, [name]: e.target.value }))}
    className={INPUT} />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">User Management</h1>
        <button onClick={() => { setShowCreateModal(true); setFormData(emptyForm); }}
          className="flex items-center justify-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto">
          + Add User
        </button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" style={card}>
            <h2 className="text-[16px] font-semibold text-[#3b3b3b] mb-5">Buat User Baru</h2>
            <div className="space-y-4">
              {[['full_name', 'Nama Lengkap'], ['email', 'Email'], ['password', 'Password (min 6 karakter)']].map(([name, label]) => (
                <div key={name}>
                  <label className={LABEL}>{label} *</label>
                  {f(name)}
                </div>
              ))}
              <div>
                <label className={LABEL}>Role *</label>
                <select value={formData.role} onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
                  className={INPUT}>
                  {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowCreateModal(false); setFormData(emptyForm); }}
                className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleCreateUser}
                className="px-4 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors">
                Buat User
              </button>
            </div>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a]">Tidak ada user ditemukan.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden" style={card}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5e5]">
                  {['Nama', 'Email', 'Role', 'Created At', 'Actions'].map(h => (
                    <th key={h} className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                    {editingUser?.id === user.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input type="text" value={formData.full_name} onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))} className={INPUT} />
                        </td>
                        <td className="px-4 py-2">
                          <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} className={INPUT} />
                        </td>
                        <td className="px-4 py-2">
                          <select value={formData.role} onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))} className={INPUT}>
                            {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a]">{fmtDate(user.created_at)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <button onClick={handleSave} className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] mr-3 transition-colors">Simpan</button>
                          <button onClick={() => { setEditingUser(null); setFormData(emptyForm); }} className="text-[12px] font-medium text-[#8a8a8a] hover:text-[#646464] transition-colors">Batal</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-[#ecf8fb] flex items-center justify-center text-[11px] font-bold text-[#2a798c] flex-shrink-0">
                              {(user.full_name || user.email || 'U')[0].toUpperCase()}
                            </div>
                            <span className="text-[13px] font-medium text-[#3b3b3b]">{user.full_name || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a]">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30 capitalize">
                            {user.role?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">{fmtDate(user.created_at)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button onClick={() => handleEdit(user)} className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] mr-3 transition-colors">Edit</button>
                          {user.id !== profile?.id && (
                            <button onClick={() => handleDelete(user.id)} className="text-[12px] font-medium text-[#c62828] hover:text-red-700 transition-colors">Hapus</button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
